import type { DependencyList } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isObject, shallowEqual } from '../helpers/common';

export function useShallowDepsCompare(deps?: DependencyList) {
  const depsRef = useRef(deps);

  if (depsRef.current === deps) return depsRef.current;

  function reset() {
    depsRef.current = deps;
  }

  if (
    depsRef &&
    depsRef.current &&
    deps &&
    depsRef.current.length === deps.length
  ) {
    const differ = deps.some((newValue, index) => {
      if (depsRef.current == null) return true;

      const oldValue = depsRef.current[index];

      if (typeof oldValue !== typeof newValue) {
        return true;
      }
      if (Array.isArray(newValue) || isObject(newValue)) {
        if (!shallowEqual(oldValue, newValue)) {
          return true;
        }
      } else if (oldValue !== newValue) {
        return true;
      }

      return false;
    });

    if (differ) reset();
  } else {
    reset();
  }

  return depsRef.current;
}

export function useShallowMemo<T>(
  factory: () => T,
  deps: DependencyList | undefined
): T {
  // eslint-disable-next-line
  return useMemo(factory, useShallowDepsCompare(deps));
}

export function useShallowCallback<T extends (...args: any[]) => any>(
  callback: T,
  deps: DependencyList
): T {
  // eslint-disable-next-line
  return useShallowMemo(() => callback, deps);
}

export function useMount() {
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  return isMounted;
}

export function useMutable<T>(value: T) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  });

  return ref;
}

export function useCreateScript(url: string, condition: boolean) {
  useEffect(() => {
    if (condition == null || !condition) return;

    const script = document.createElement('script');

    script.src = url;
    script.async = true;
    script.type = 'text/javascript';

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [url, condition]);
}

export function useInlineScript(
  content: string,
  condition: boolean,
  id?: string
) {
  useEffect(() => {
    if (!condition) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    if (id) script.id = id;
    script.innerHTML = content;

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [content, condition, id]);
}

export function useCreateNoscript(nosript: string, condition: boolean) {
  useEffect(() => {
    if (condition == null || !condition) return;

    const noscriptBlock = document.getElementById('noscript');

    if (noscriptBlock == null) return;

    noscriptBlock.innerHTML = nosript.trim();
  }, [nosript, condition]);
}

export function useToggle(
  initialValue = false
): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => {
    setValue((v) => !v);
  }, []);

  const setState = useCallback((value: boolean) => {
    setValue(value);
  }, []);

  return [value, toggle, setState];
}

export enum DeviceMode {
  PORTRAIT = 0,
  LANDSCAPE = 1,
}
